import React, { useState } from 'react';
import { MdDownload, MdPictureAsPdf, MdTableChart } from 'react-icons/md';
import { Modal } from './Modal';
import { cn } from '../../lib/utils';
import type { RfqExportFormat } from '../../utils/rfq-data-export';

interface ExportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: RfqExportFormat) => void | Promise<void>;
  recordCount: number;
  title?: string;
  entityLabel?: string;
}

export function ExportDataModal({
  isOpen,
  onClose,
  onExport,
  recordCount,
  title = 'Export Data',
  entityLabel = 'RFQ record',
}: ExportDataModalProps) {
  const [format, setFormat] = useState<RfqExportFormat>('excel');
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (recordCount <= 0 || busy) return;
    setBusy(true);
    try {
      await onExport(format);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="small">
      <div className="p-6 space-y-5">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Choose a download format for{' '}
          <span className="font-semibold text-[hsl(var(--foreground))]">
              {recordCount} {entityLabel}{recordCount === 1 ? '' : 's'}
          </span>{' '}
          (current filters applied).
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setFormat('excel')}
            className={cn(
              'flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all',
              format === 'excel'
                ? 'border-[#0088cc] bg-[#0088cc]/10 ring-1 ring-[#0088cc]/40'
                : 'border-[hsl(var(--border))] hover:border-[#0088cc]/40 hover:bg-[hsl(var(--muted))]/40',
            )}
          >
            <MdTableChart className="w-6 h-6 text-emerald-500" />
            <div>
              <p className="text-sm font-bold text-[hsl(var(--foreground))]">Excel (.xlsx)</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                Spreadsheet for filtering and analysis
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setFormat('pdf')}
            className={cn(
              'flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all',
              format === 'pdf'
                ? 'border-[#0088cc] bg-[#0088cc]/10 ring-1 ring-[#0088cc]/40'
                : 'border-[hsl(var(--border))] hover:border-[#0088cc]/40 hover:bg-[hsl(var(--muted))]/40',
            )}
          >
            <MdPictureAsPdf className="w-6 h-6 text-rose-500" />
            <div>
              <p className="text-sm font-bold text-[hsl(var(--foreground))]">PDF (.pdf)</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                Printable landscape report
              </p>
            </div>
          </button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || recordCount <= 0}
            onClick={() => void handleConfirm()}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#0088cc] hover:bg-[#0077b3]',
              (busy || recordCount <= 0) && 'opacity-60 cursor-not-allowed',
            )}
          >
            <MdDownload className="w-4 h-4" />
            {busy ? 'Preparing…' : 'Download'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
